<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, watch, ref, reactive, provide } from "vue";
import { useRoute, useRouter } from "vue-router";
import ContractAddEditForm from "@/components/models/practice/forms/ContractAddEditForm.vue";
import CompleteStep from "@/components/models/assistant/CompleteStep.vue";
import type { AssistantData } from "@/types/assistant";
import type { TCompany } from "@/api/models/company/company";
import type { TPractice } from "@/api/models/practice/practice";
import { useSettingsStore } from "@/stores/settingsStore";
import { ETabs } from "@/api/shared/administrative.ts";
import {
  useCanAccessAdministration,
  useWizardStepPermissions,
} from "@/composables/shared/useSectionPermissions";
import { definePage } from "unplugin-vue-router/runtime";
import CompanyAddEditForm from "@/components/models/company/forms/CompanyAddEditForm.vue";
import PracticeAddEditForm from "@/components/models/practice/forms/PracticeAddEditForm.vue";
import { EPracticeStaffSettingsKey } from "@/api/models/practice/practiceStaffSettings";
import BaseDialog from "@/components/shared/dialogs/BaseDialog.vue";
import BaseDialogActions from "@/components/shared/dialogs/BaseDialogActions.vue";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";

definePage({
  meta: {
    title: "generic.entity.administrativeAssistant",
    checkPrivileges: () => {
      return (
        useCanAccessAdministration().value &&
        Object.values(useWizardStepPermissions().value).some(Boolean)
      );
    },
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const settingsStore = useSettingsStore();

const assistantData = reactive<AssistantData>({
  orthoCompany: null,
  gpCompany: null,
  orthoPractice: null,
  gpPractice: null,
});
const privileges = useWizardStepPermissions();
provide("assistantData", assistantData);
const steps = computed(() =>
  [
    {
      title: t("models.assistant.page.steps.orthoCompanies.title"),
      addTitle: t("models.assistant.page.steps.orthoCompanies.addTitle"),
      permission: privileges.value[ETabs.ORTHO_PRACTICES] === true,
      component: CompanyAddEditForm,
      formForWhatType: "ortho",
      hintModal: {
        title: t("models.assistant.page.steps.orthoCompanies.hintModal.title"),
        text: t("models.assistant.page.steps.orthoCompanies.hintModal.text"),
      },
    },
    {
      title: t("models.assistant.page.steps.gpCompanies.title"),
      addTitle: t("models.assistant.page.steps.gpCompanies.addTitle"),
      permission: privileges.value[ETabs.GP_PRACTICES] === true,
      component: CompanyAddEditForm,
      formForWhatType: "gp",
      hintModal: {
        title: t("models.assistant.page.steps.gpCompanies.hintModal.title"),
        text: t("models.assistant.page.steps.gpCompanies.hintModal.text"),
      },
    },
    {
      title: t("models.assistant.page.steps.contracts.title"),
      addTitle: t("models.assistant.page.steps.contracts.addTitle"),
      permission: privileges.value[ETabs.CONTRACTS] === true,
      component: ContractAddEditForm,
      hintModal: {
        title: t("models.assistant.page.steps.contracts.hintModal.title"),
        text: t("models.assistant.page.steps.contracts.hintModal.text"),
      },
    },
    {
      title: t("models.assistant.page.steps.orthoPractices.title"),
      addTitle: t("models.assistant.page.steps.orthoPractices.addTitle"),
      permission: privileges.value[ETabs.ORTHO_OFFICES] === true,
      component: PracticeAddEditForm,
      formForWhatType: "ortho",
      allowMultiple: true,
      hintModal: {
        title: t("models.assistant.page.steps.orthoPractices.hintModal.title"),
        text: t("models.assistant.page.steps.orthoPractices.hintModal.text"),
        bullets: [
          t("models.assistant.page.steps.orthoPractices.hintModal.bullets[0]"),
          t("models.assistant.page.steps.orthoPractices.hintModal.bullets[1]"),
          t("models.assistant.page.steps.orthoPractices.hintModal.bullets[2]"),
          t("models.assistant.page.steps.orthoPractices.hintModal.bullets[3]"),
          t("models.assistant.page.steps.orthoPractices.hintModal.bullets[4]"),
        ],
        afterText: t("models.assistant.page.steps.orthoPractices.hintModal.afterText"),
      },
    },
    {
      title: t("models.assistant.page.steps.gpPractices.title"),
      addTitle: t("models.assistant.page.steps.gpPractices.addTitle"),
      permission: privileges.value[ETabs.GP_OFFICES] === true,
      component: PracticeAddEditForm,
      formForWhatType: "gp",
      allowMultiple: true,
      hintModal: {
        title: t("models.assistant.page.steps.gpPractices.hintModal.title"),
        text: t("models.assistant.page.steps.gpPractices.hintModal.text"),
        bullets: [
          t("models.assistant.page.steps.gpPractices.hintModal.bullets[0]"),
          t("models.assistant.page.steps.gpPractices.hintModal.bullets[1]"),
          t("models.assistant.page.steps.gpPractices.hintModal.bullets[2]"),
          t("models.assistant.page.steps.gpPractices.hintModal.bullets[3]"),
        ],
        afterText: t("models.assistant.page.steps.gpPractices.hintModal.afterText"),
      },
    },
    {
      title: t("models.assistant.page.steps.complete.title"),
      addTitle: t("models.assistant.page.steps.complete.addTitle"),
      permission: privileges.value[ETabs.MEMBERS] === true,
      component: CompleteStep,
      hideTitle: true,
      hideActions: true,
    },
  ].filter((step) => step.permission)
);

const totalSteps = computed(() => steps.value.length);
// this should probably not be a number, but a named step, and it should not be possible to
// move across steps using url (current state should be saved somewhere in a store), but leaving both
// for now for easier development
const currentStepIndex = computed(() =>
  Number.parseInt((route.params as { step: string })?.step || "1", 10)
);
const currentStep = computed(() => steps.value[currentStepIndex.value - 1]);
const hintDialog = ref(false);
const dismissedHintSteps = ref(new Set<number>());
const successActive = ref(false);
const currentStepForm = ref<{ submit: () => void } | null>(null);
const saveProcessing = ref(false);

const handleSave = () => {
  if (currentStepForm.value) {
    currentStepForm.value.submit();
  }
};

const handleSuccess = (value: unknown) => {
  addToBag(value);
  saveProcessing.value = false;
  successActive.value = true;
  setTimeout(() => {
    successActive.value = false;
    router.push({
      name: "/administrative/assistant/[[step]]",
      params: { step: currentStepIndex.value + 1 },
    });
  }, 1500);
};

function addToBag(data: unknown) {
  const stepType = currentStep.value?.formForWhatType;
  const component = currentStep.value?.component;

  if (component === CompanyAddEditForm) {
    if (stepType === "ortho") assistantData.orthoCompany = data as TCompany;
    if (stepType === "gp") assistantData.gpCompany = data as TCompany;
  }

  if (component === PracticeAddEditForm) {
    if (stepType === "ortho") assistantData.orthoPractice = data as TPractice;
    if (stepType === "gp") assistantData.gpPractice = data as TPractice;
  }
}

watch(
  () => (route.params as { step: string }).step,
  () => {
    const stepIndex = currentStepIndex.value;

    if (
      !settingsStore[EPracticeStaffSettingsKey.ADMINISTRATIVE_ASSISTANT_HINTS_DISABLED] &&
      currentStep.value?.hintModal &&
      !dismissedHintSteps.value.has(stepIndex)
    ) {
      setTimeout(() => {
        if (!dismissedHintSteps.value.has(stepIndex)) {
          hintDialog.value = true;
        }
      }, 700);
    }
  },
  { immediate: true }
);

watch(hintDialog, (open) => {
  if (!open) {
    dismissedHintSteps.value.add(currentStepIndex.value);
  }
});
</script>

<template>
  <div class="tw:!h-full">
    <div class="tw:bg-surface tw:rounded-lg tw:!px-8 tw:!py-7">
      <div class="tw:flex tw:justify-center tw:items-center">
        <div class="tw:text-sm">
          {{
            t("models.assistant.page.stepsCount", { current: currentStepIndex, total: totalSteps })
          }}
        </div>
        <div class="tw:flex-grow tw:!ml-4">
          <v-progress-linear
            :model-value="currentStepIndex"
            :max="totalSteps"
            :height="6"
            rounded
            class="custom-gradient-progress"
            style="background: none"
          />
        </div>
      </div>
      <div class="tw:!mt-4 tw:text-sm tw:flex">
        <div
          v-for="(step, index) in steps"
          :key="`step-${index}`"
          :class="{ 'tw:text-unuser': index < currentStepIndex }"
        >
          {{ step?.title }}
          <v-icon
            v-if="index < totalSteps - 1"
            size="x-small"
            icon="mdi-chevron-right"
            class="tw:!mx-[0.2rem]"
          />
        </div>
      </div>
    </div>

    <transition
      name="fade-slide-up"
      mode="out-in"
      appear
    >
      <div
        v-if="!successActive"
        :key="currentStepIndex"
        class="tw:bg-surface tw:rounded-lg tw:!py-7 tw:!mt-4"
      >
        <div
          v-if="!currentStep?.hideTitle"
          class="tw:!px-8 tw:flex tw:items-end"
        >
          <div class="tw:text-3xl tw:font-bold">
            {{ currentStep?.addTitle }}
          </div>
        </div>
        <div
          v-if="!currentStep?.hideTitle"
          class="tw:!my-4"
        >
          <v-divider
            color="background"
            opacity="100"
            thickness="2"
          />
        </div>
        <div class="tw:!px-8">
          <component
            ref="currentStepForm"
            :key="currentStepIndex"
            :is="currentStep?.component"
            hide-action-buttons
            hide-title
            stay-after-submit
            :formForWhatType="currentStep?.formForWhatType"
            :multiple="currentStep?.allowMultiple"
            @submit:success="handleSuccess"
            @submit:error="saveProcessing = false"
            @submit:processing="saveProcessing = true"
          />
          <div
            v-if="!currentStep?.hideActions"
            class="tw:flex tw:!mt-6"
          >
            <PrimaryButton
              size="x-large"
              :disabled="saveProcessing"
              :loading="saveProcessing"
              @click="handleSave"
            >
              {{ t("models.assistant.page.actions.nextStep") }}
            </PrimaryButton>
            <v-btn
              rounded
              variant="text"
              color="primary"
              size="x-large"
              disabled
              class="tw:!mx-2"
            >
              {{ t("models.assistant.page.actions.previousStep") }}
            </v-btn>
            <v-btn
              rounded
              variant="text"
              color="primary"
              size="x-large"
              :disabled="saveProcessing"
              :to="{ name: '/administrative/[[tab]]', params: { tab: ETabs.ASSISTANT } }"
            >
              {{ t("models.assistant.page.actions.cancel") }}
            </v-btn>
          </div>
        </div>
      </div>
      <div
        v-else
        class="tw:bg-surface tw:rounded-lg tw:!h-[80%] tw:!w-full tw:flex tw:justify-center tw:items-center tw:!py-7 tw:!mt-4"
      >
        <v-icon
          icon="mdi-check-circle"
          color="secondary"
          size="90"
        />
      </div>
    </transition>
    <BaseDialog
      v-if="currentStep?.hintModal"
      v-model="hintDialog"
      icon="mdi-note-plus"
    >
      <template #title>
        {{ currentStep.hintModal?.title }}
      </template>
      <template #body>
        <div>{{ currentStep.hintModal?.text }}</div>
        <div
          v-if="currentStep.hintModal?.bullets"
          class="tw:!my-4"
        >
          <div
            v-for="(bullet, index) in currentStep.hintModal?.bullets"
            :key="`bullet-${index}`"
            class="tw:font-bold"
          >
            {{ index + 1 }}. {{ bullet }}
          </div>
        </div>
        <div v-if="currentStep.hintModal?.afterText">
          {{ currentStep.hintModal?.afterText }}
        </div>
      </template>
      <template #actions>
        <BaseDialogActions
          :secondary="{
            icon: 'mdi-lightbulb-off-outline',
            text: t('models.assistant.page.actions.turnOffHints'),
            action: () => {
              settingsStore[EPracticeStaffSettingsKey.ADMINISTRATIVE_ASSISTANT_HINTS_DISABLED] =
                true;
              hintDialog = false;
            },
          }"
          :primary="{
            icon: 'mdi-arrow-right',
            text: t('models.assistant.page.actions.continue'),
            action: () => {
              hintDialog = false;
            },
          }"
        />
      </template>
    </BaseDialog>
  </div>
</template>

<style scoped>
:deep(.custom-gradient-progress .v-progress-linear__determinate) {
  background-image: linear-gradient(to right, #14b8a6, #3b82f6);
  /* teal-400 to blue-600 */
}

.fade-slide-up-enter-active,
.fade-slide-up-leave-active {
  transition: all 0.3s ease-out;
}

.fade-slide-up-enter-from,
.fade-slide-up-leave-to {
  opacity: 0;
  transform: translateY(24px);
  /* ~translate-y-6 */
}

.fade-slide-up-enter-to,
.fade-slide-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
